import { BASE_URL } from 'mk/settings';
import { getArticleTitleAsString } from 'mk2/apps/blogs/containers/Article/Article.helpers';
import { getPostDate } from 'mk2/apps/blogs/helpers';
import {
    blogsAlbumDetailUrl,
    blogsAlbumPhotoUrl,
    blogsAutoMessagePhotoUrl,
    blogsAutoMessageUrl,
    blogsShareMessageUrl,
    blogsShortMessagePhotoUrl,
    blogsShortMessageUrl,
} from 'mk2/apps/blogs/urls';
import { articleUrl as blogsArticleUrl } from 'mk2/apps/blogs/utils';
import { homepageUrl } from 'mk2/apps/home/urls';
import { getFirstParagraphsPlaintext } from 'mk2/helpers/article_ast/utils';
import {
    getCoverOrFirstPhoto,
    getPhotoResizedDimensions,
    getUrlOfCoverImageOrFirstImageOrFallback,
    getUrlOfFirstImageOrFallback,
    organizationModrykonikWithoutContext,
} from 'mk2/helpers/structuredData';
import { YMDHMSIso } from 'mk2/helpers/timeformat';
import { fbMorykonikProfileUrl, url } from 'mk2/helpers/urls';
import { BlogPostEntity, BlogPostType, ImageEntity } from 'mk2/schemas';
import { Article, WithContext } from 'schema-dts';

export function articleBlogs(baseUrl: string, article: BlogPostEntity): WithContext<Article> {
    // according to https://developers.google.com/search/docs/data-types/article#amp
    return {
        '@context': 'https://schema.org',
        '@type': 'Article',
        '@id': baseUrl + blogsArticleUrl(article),
        'headline': getArticleTitleAsString(article.titleAST),
        'description': getFirstParagraphsPlaintext(article.contentAST),
        'image': getUrlOfCoverImageOrFirstImageOrFallback(article.contentAST, article.photos),

        'datePublished': article.firstPublishedAt
            ? YMDHMSIso(article.firstPublishedAt)
            : YMDHMSIso(getPostDate(article)),
        'dateModified': YMDHMSIso(getPostDate(article)),

        'author': {
            '@type': 'Person',
            'name': article.user.username,
        },
        'mainEntityOfPage': {
            '@type': 'WebPage',
            '@id': baseUrl + blogsArticleUrl(article),
        },
        'publisher': organizationModrykonikWithoutContext(),
    };
}
export function articleBlogsOG(baseUrl: string, article: BlogPostEntity) {
    const articleUrl = blogsArticleUrl(article);
    const mainPhoto = getCoverOrFirstPhoto(article.contentAST, article.photos);
    const dimensions = mainPhoto ? getPhotoResizedDimensions(mainPhoto, 1600) : null;

    // according to https://developers.facebook.com/docs/reference/opengraph/object-type/article/
    return {
        'og:type': 'article',

        'og:title': getArticleTitleAsString(article.titleAST),
        'og:description': getFirstParagraphsPlaintext(article.contentAST),

        'og:image': getUrlOfCoverImageOrFirstImageOrFallback(article.contentAST, article.photos),
        ...(dimensions && {
            'og:image:width': String(dimensions.width),
            'og:image:height': String(dimensions.height),
        }),
        'og:url': baseUrl + articleUrl,

        'article:author': fbMorykonikProfileUrl(),
        'article:content_tier': 'free',

        'article:published_time': article.firstPublishedAt
            ? YMDHMSIso(article.firstPublishedAt)
            : YMDHMSIso(getPostDate(article)),
        'article:modified_time': YMDHMSIso(getPostDate(article)),
        'article:publisher': BASE_URL + homepageUrl,
        ...(article.advancedArticle?.category?.name && {
            'article:section': article.advancedArticle.category.name,
        }),
    };
}

export function shortMessageBlogsOG(baseUrl: string, post: BlogPostEntity) {
    const username = post.user?.username || post.author?.username;
    const postSlug = post.slug;

    const pageUrl = post.type === BlogPostType.AUTOMATIC_MESSAGE
        ? url(blogsAutoMessageUrl, { username, postSlug })
        : post.type === BlogPostType.SHARE
            ? url(blogsShareMessageUrl, { username, postSlug })
            : url(blogsShortMessageUrl, { username, postSlug });

    // Short messages store photos in images[].photo, not in photos[]
    const mainPhoto = post.images?.length > 0 ? post.images[0].photo : null;
    const dimensions = mainPhoto ? getPhotoResizedDimensions(mainPhoto, 1600) : null;

    return {
        'og:type': 'website',

        'og:image': getUrlOfFirstImageOrFallback(mainPhoto),
        ...(dimensions && {
            'og:image:width': String(dimensions.width),
            'og:image:height': String(dimensions.height),
        }),
        'og:url': baseUrl + pageUrl,
    };
}

export function albumBlogsOG(baseUrl: string, post: BlogPostEntity) {
    const username = post.user?.username || post.author?.username;
    const postSlug = post.slug;

    const pageUrl = url(blogsAlbumDetailUrl, { username, postSlug });

    // Use first image from album
    const mainPhoto = post.images?.length > 0 ? post.images[0].photo : null;
    const dimensions = mainPhoto ? getPhotoResizedDimensions(mainPhoto, 1600) : null;

    return {
        'og:type': 'website',

        'og:image': getUrlOfFirstImageOrFallback(mainPhoto),
        ...(dimensions && {
            'og:image:width': String(dimensions.width),
            'og:image:height': String(dimensions.height),
        }),
        'og:url': baseUrl + pageUrl,
    };
}

export function imageBlogsOG(baseUrl: string, post: BlogPostEntity, image: ImageEntity) {
    const pageUrl = post.type === BlogPostType.ALBUM
        ? url(blogsAlbumPhotoUrl, {username: post.author.username, postSlug: post.slug, imageId: image.id})
        : post.type === BlogPostType.AUTOMATIC_MESSAGE
            ? url(blogsAutoMessagePhotoUrl, {username: post.author.username, postSlug: post.slug, imageId: image.id})
            : url(blogsShortMessagePhotoUrl, {username: post.author.username, postSlug: post.slug, imageId: image.id});

    const dimensions = image?.photo ? getPhotoResizedDimensions(image.photo, 1600) : null;

    // according to https://developers.facebook.com/docs/reference/opengraph/object-type/article/
    return {
        'og:type': 'website',

        'og:title': image.title || (post.perexAST ? getFirstParagraphsPlaintext(post.perexAST) : ''),
        'og:description': post.contentAST ? getFirstParagraphsPlaintext(post.contentAST) : '',

        'og:image': getUrlOfFirstImageOrFallback(image.photo),
        ...(dimensions && {
            'og:image:width': String(dimensions.width),
            'og:image:height': String(dimensions.height),
        }),
        'og:url': baseUrl + pageUrl,
    };
}
